/* eslint-disable react/jsx-props-no-spreading */

"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import { useSelector } from "react-redux";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import ActionButton from "@/components/molecules/action-button/action-button";
import { RenewalPage } from "@/config/app-constant";
import useGtmViewQuoteData from "@/hooks/gtm/use-gtm-view-quote";
import { useRenewalStateContext } from "@/providers/renewal-state-provider";
import { RenewalFooterContentType } from "@/schema/renewal-page/page-content.schema";
import { RootState } from "@/store/store";
import { RenewalSectionType } from "@/types/renewals";
import QuoteDataMap from "@/utils/gtm/quote-data-map";

import DontWantToRenewModal from "./modals/dont-want-to-renew-modal/dont-want-to-renew-modal";
import { ModalContent } from "./modals/modal-content/modal-content";
import { ModalWrapper } from "./modals/modal-wrapper";

type LandingFooterType = {
  footerContent: RenewalFooterContentType;
};

type ModalType = 0 | 1 | 2 | 3;

function LandingFooter({ footerContent }: Readonly<LandingFooterType>) {
  const {
    continueCtaLable,
    dontWantRenewLable,
    medicalModal,
    confirmMedicalModal,
    dontMeetConditionMedicalModal,
    dontWantToRenewModal,
  } = footerContent;
  const router = useRouter();
  const { referenceId, attributes } = useSelector(
    (state: RootState) => state.quote
  );
  const { isLoading, setExpandedSections, expandedSections } =
    useRenewalStateContext();
  const [isOpened, setIsOpened] = useState(false);
  const [isDontWantTotRenewModalOpen, setIsDontWantTotRenewModalOpen] =
    useState(false);
  const [modalType, setModalType] = useState<ModalType>(0);
  const [shouldTriggerGtm, setShouldTriggerGtm] = useState(false);
  /**
   * 0: No modal
   * 1: Medical modal
   * 2: confirmation modal
   * 3: don't meet conditions modal
   * */

  let gtmQuoteViewData = null;
  if (referenceId) {
    gtmQuoteViewData = QuoteDataMap(
      attributes,
      "renewal_add_to_cart",
      "renewal_quote",
      referenceId,
      "renewal_journey"
    );
  }

  useGtmViewQuoteData({
    quoteData: gtmQuoteViewData,
    isReady: shouldTriggerGtm,
  });

  const { current } = useWizard();

  // Handler for back button click
  const handleBackClick = () => {
    router.push(current.prev ?? process.env.NEXT_PUBLIC_MY_ACCOUNT_URL!);
  };

  const handleContinue = () => {
    const isMedicalAccordionOpen = sessionStorage.getItem(
      "isMedicalAccordionOpen"
    );
    if (
      isMedicalAccordionOpen === null ||
      isMedicalAccordionOpen === undefined
    ) {
      setModalType(1);
    } else {
      setModalType(2);
    }
    setIsOpened(true);
  };

  const handleMedicalReminderModalClick = () => {
    const itemId: RenewalSectionType = "medical-conditions";
    const newActiveArr = [...expandedSections, itemId];
    setExpandedSections(newActiveArr);
    sessionStorage.setItem("expandedSections", JSON.stringify(newActiveArr));
    sessionStorage.setItem("isMedicalAccordionOpen", "true");
    setIsOpened(false);
    const focusElement = document.getElementById(
      "accordion-control-medical-conditions"
    );
    if (focusElement) {
      focusElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleConfirmMedicalModalClick = () => {
    setIsOpened(false);
    setShouldTriggerGtm(true);
    router.push(current.next ?? "");
  };
  const confirmMedicalSecondaryClick = () => {
    setModalType(3);
  };

  const formatCallNumber = (number: string = "0330 828 6345") => {
    const numberToCall = number.replace(/\D/g, "");
    const telUri = `tel:${numberToCall}`;
    return telUri;
  };

  const dontMeetConditionClick = () => {
    window.location.href = formatCallNumber(
      dontMeetConditionMedicalModal.contactNumber
    );
  };
  const dontMeetConditionSecondaryClick = () => {
    setModalType(2);
  };

  const renderModalContent = {
    0: null,
    1: (
      <ModalContent
        {...medicalModal}
        onClick={handleMedicalReminderModalClick}
        id="medical-reminder"
      />
    ),
    2: (
      <ModalContent
        {...confirmMedicalModal}
        id="confirmation"
        isSecondaryCtaEnabled
        onClick={handleConfirmMedicalModalClick}
        onClickSecondary={confirmMedicalSecondaryClick}
      />
    ),
    3: (
      <ModalContent
        id="dont-meet-conditions"
        isSecondaryCtaEnabled
        {...dontMeetConditionMedicalModal}
        contactNumber={formatCallNumber(
          dontMeetConditionMedicalModal.contactNumber
        )}
        onClick={dontMeetConditionClick}
        onClickSecondary={dontMeetConditionSecondaryClick}
      />
    ),
  };

  return (
    <div className="mx-auto md:grid w-fit justify-center">
      <footer className="w-full md:w-[414px] lg:h-spacing-6xl mx-auto pb-spacing-xs md:pb-spacing-s lg:pb-[0px] flex flex-col justify-center items-center gap-spacing-l">
        {/* Back Arrow Button */}

        <div className="w-full fixed z-[10] bottom-0 left-0 lg:relative flex flex-col justify-between h-fit">
          <div className=" backdrop-blur-[1.5px] bg-interactive-overlay-image lg:hidden absolute h-full w-full z-[1] top-0 right-0" />
          <div className="py-spacing-s lg:!p-0 md:w-[414px] mx-auto">
            <ActionButton
              page={RenewalPage}
              showBackBtn
              submitButtonClick={handleContinue}
              backButtonClick={handleBackClick}
              ctaButtonText={continueCtaLable}
              disableMain={isLoading}
              disableBackBtn={isLoading}
            />
          </div>
        </div>

        {/* Don't want to renew? Link */}
        <LabelButtonWrapper
          data-testid="dont-want-to-renew-link"
          id="dont-want-to-renew-link"
          isClickableDisabled={isLoading}
          isDisabled={isLoading}
          onClick={() => setIsDontWantTotRenewModalOpen(true)}
          label={dontWantRenewLable}
          className={`underline underline-offset-[2px] ${
            isLoading
              ? "text-text-functional-subtle "
              : "text-interactive-secondary-default font-semibold "
          } font-[600] focus:outline-none focus:ring-0 `}
        />
      </footer>

      {isOpened && (
        <ModalWrapper
          isOpen={isOpened}
          setIsOpen={setIsOpened}
          isShowIcon={modalType !== 2}
          isClosedByCtaOnly={modalType === 2}
        >
          {renderModalContent[modalType]}
        </ModalWrapper>
      )}

      {isDontWantTotRenewModalOpen && (
        <DontWantToRenewModal
          modalContent={dontWantToRenewModal}
          setIsOpen={setIsDontWantTotRenewModalOpen}
          isOpen={isDontWantTotRenewModalOpen}
        />
      )}
    </div>
  );
}

export default LandingFooter;
